// Advanced KB search filters - 150 lines ✅

import { Filter, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { D365_MODULES, KB_CATEGORIES } from '@/lib/constants'
import { cn } from '@/lib/utils'
import type { SearchFilters } from '@/types/knowledge'

interface SearchFiltersProps {
  filters: SearchFilters
  onFiltersChange: (filters: SearchFilters) => void
  showFilters: boolean
  onToggle: () => void
  className?: string
}

export function SearchFiltersPanel({
  filters,
  onFiltersChange,
  showFilters,
  onToggle,
  className,
}: SearchFiltersProps): JSX.Element {
  const activeFilterCount = [
    filters.modules?.length ?? 0,
    filters.categories?.length ?? 0,
    filters.status?.length ?? 0,
    filters.validated !== undefined ? 1 : 0,
  ].reduce((sum, count) => sum + count, 0)

  const handleModuleToggle = (module: string): void => {
    const current = filters.modules || []
    const updated = current.includes(module)
      ? current.filter((m) => m !== module)
      : [...current, module]

    onFiltersChange({
      ...filters,
      modules: updated.length > 0 ? updated : undefined,
    })
  }

  const handleCategoryToggle = (category: string): void => {
    const current = filters.categories || []
    const updated = current.includes(category as any)
      ? current.filter((c) => c !== category)
      : [...current, category as any]

    onFiltersChange({
      ...filters,
      categories: updated.length > 0 ? updated : undefined,
    })
  }

  const handleStatusToggle = (status: string): void => {
    const current = filters.status || []
    const updated = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status]

    onFiltersChange({
      ...filters,
      status: updated.length > 0 ? updated : undefined,
    })
  }

  const clearAllFilters = (): void => {
    onFiltersChange({})
  }

  const RESOLUTION_STATUSES = [
    { value: 'validated', label: 'Validated' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'deprecated', label: 'Deprecated' },
  ]

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-4">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onToggle}
          className="border-kaycoo-teal-dark text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale"
          aria-label="Toggle filters"
          aria-expanded={showFilters}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <Badge className="ml-2 bg-kaycoo-teal text-white">
              {activeFilterCount}
            </Badge>
          )}
        </Button>

        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-kaycoo-charcoal-2 hover:text-kaycoo-burgundy"
          >
            <X className="h-4 w-4 mr-1" />
            Clear all
          </Button>
        )}
      </div>

      {showFilters && (
        <Card className="border-kaycoo-white-3 bg-white p-4 space-y-4">
          {/* Resolution Status */}
          <div className="space-y-2">
            <Label className="text-kaycoo-charcoal font-medium">Resolution Status</Label>
            <div className="flex flex-wrap gap-2">
              {RESOLUTION_STATUSES.map((status) => {
                const isActive = filters.status?.includes(status.value)
                return (
                  <Badge
                    key={status.value}
                    onClick={() => handleStatusToggle(status.value)}
                    className={cn(
                      'cursor-pointer transition-colors',
                      isActive
                        ? 'bg-kaycoo-teal text-white border-kaycoo-teal'
                        : 'bg-kaycoo-white-2 text-kaycoo-charcoal border-kaycoo-white-3 hover:border-kaycoo-teal'
                    )}
                  >
                    {status.label}
                  </Badge>
                )
              })}
            </div>
          </div>

          {/* Modules */}
          <div className="space-y-2">
            <Label className="text-kaycoo-charcoal font-medium">D365 F&O Modules</Label>
            <div className="flex flex-wrap gap-2">
              {D365_MODULES.map((module) => {
                const isActive = filters.modules?.includes(module)
                return (
                  <Badge
                    key={module}
                    onClick={() => handleModuleToggle(module)}
                    className={cn(
                      'cursor-pointer transition-colors',
                      isActive
                        ? 'bg-kaycoo-blue text-white border-kaycoo-blue'
                        : 'bg-kaycoo-white-2 text-kaycoo-charcoal border-kaycoo-white-3 hover:border-kaycoo-blue'
                    )}
                  >
                    {module}
                  </Badge>
                )
              })}
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-2">
            <Label className="text-kaycoo-charcoal font-medium">Categories</Label>
            <div className="flex flex-wrap gap-2">
              {KB_CATEGORIES.map((category) => {
                const isActive = filters.categories?.includes(category.value)
                return (
                  <Badge
                    key={category.value}
                    onClick={() => handleCategoryToggle(category.value)}
                    className={cn(
                      'cursor-pointer transition-colors',
                      isActive
                        ? 'bg-kaycoo-burgundy text-white border-kaycoo-burgundy'
                        : 'bg-kaycoo-white-2 text-kaycoo-charcoal border-kaycoo-white-3 hover:border-kaycoo-burgundy'
                    )}
                  >
                    {category.label}
                  </Badge>
                )
              })}
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
